import { useMemo } from 'react';
import { Link } from 'react-router';
import { cn } from '@rocco/utils/cn';
import { ReactComponent as ChevronLeft } from '@rocco/icons/svg/arrow/arrow-chevron-left.svg';

interface BackButtonProps {
    parentCategoryName: string;
    parentCategoryLink: string;
    onClick?: () => void;
    /**
     * Visual style. `dark` = solid dark pill (5.0 light browse); `light` = solid
     * light-grey pill (5.0 dark/editorial browse); `outline` = default.
     */
    variant?: 'outline' | 'dark' | 'light';
}

/**
 * Back button component for Products page filter bar navigation.
 * Navigates to the parent category in the product hierarchy.
 *
 * @example
 * ```tsx
 * <BackButton
 *   parentCategoryName="Ceiling Lighting"
 *   parentCategoryLink="/products/lighting/ceiling-lighting"
 * />
 * ```
 *
 * @param parentCategoryName - Display name of parent category
 * @param parentCategoryLink - URL path to parent category
 */
export const BackButton = ({
    parentCategoryName,
    parentCategoryLink,
    onClick,
    variant = 'outline',
}: BackButtonProps) => {
    const isDark = variant === 'dark';
    const isLight = variant === 'light';
    // Validate URL to prevent XSS - only allow same-origin paths with safe protocols
    const safeLink = useMemo(() => {
        // SSR-safe check: during server-side rendering, only allow relative paths
        if (typeof window === 'undefined') {
            if (parentCategoryLink.startsWith('/')) {
                return parentCategoryLink;
            }
            return '/products';
        }

        try {
            // Parse URL to validate protocol and origin
            const url = new URL(parentCategoryLink, window.location.origin);

            // Only allow http and https protocols
            const allowedProtocols = ['http:', 'https:'];
            if (!allowedProtocols.includes(url.protocol)) {
                console.error('Unsafe protocol blocked');
                return '/products';
            }

            // Block cross-origin URLs
            if (url.origin !== window.location.origin) {
                console.error('Cross-origin navigation blocked');
                return '/products';
            }

            return url.pathname + url.search;
        } catch (error) {
            console.error('Invalid URL:', error);
            return '/products';
        }
    }, [parentCategoryLink]);

    const sharedClassName = cn(
        'flex h-10.5 shrink-0 items-center gap-1 rounded-full pr-5 pl-3.5 transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden',
        isDark &&
            'bg-[#323232] text-white hover:bg-[#484848] active:bg-[#515151]',
        isLight &&
            'bg-[#d5d5d5] text-[#181818] hover:bg-[#c8c8c8] active:bg-[#bcbcbc]',
        !isDark &&
            !isLight &&
            'border border-black hover:bg-black/5 active:bg-black/10',
    );

    const content = (
        <>
            <ChevronLeft
                aria-hidden="true"
                className={cn(
                    'size-3.5 fill-current',
                    isDark && 'text-white',
                    isLight && 'text-[#181818]',
                    !isDark && !isLight && 'text-[#323232]',
                )}
            />
            <span
                className={cn(
                    // No width cap: the pill sits in shrink-0 groups (or a
                    // horizontal scroller on mobile), so it can size to the
                    // full category name — clamping clipped long titles like
                    // "Bathroom Heating & Ventilation".
                    'text-base tracking-tighter whitespace-nowrap',
                    isDark && 'text-white',
                    isLight && 'text-[#181818]',
                    !isDark && !isLight && 'text-[#323232]',
                )}
            >
                {parentCategoryName}
            </span>
        </>
    );

    if (onClick) {
        return (
            <button
                type="button"
                onClick={onClick}
                aria-label={`Back to ${parentCategoryName}`}
                className={sharedClassName}
            >
                {content}
            </button>
        );
    }

    return (
        <Link
            to={safeLink}
            state={{ restoreScroll: true, scroll: false }}
            aria-label={`Back to ${parentCategoryName}`}
            className={sharedClassName}
        >
            {content}
        </Link>
    );
};
